<template>
  <div class="app-select" :class="selectClasses">
    <!-- Label -->
    <label v-if="label" :for="inputId" class="select-label">
      {{ label }}
      <span v-if="required" class="select-required">*</span>
    </label>

    <!-- Select wrapper -->
    <div class="select-wrapper" ref="selectRef">
      <!-- Input field -->
      <div
        class="select-input"
        :class="{
          'select-input--focused': isOpen,
          'select-input--error': hasError,
          'select-input--disabled': disabled,
        }"
        @click="handleInputClick"
      >
        <!-- Selected value or placeholder -->
        <div class="select-value">
          <template v-if="selectedOption">
            <component
              v-if="selectedOption.icon"
              :is="selectedOption.icon"
              :size="16"
              class="select-value-icon"
            />
            <span class="select-value-text">{{ selectedOption.label }}</span>
          </template>
          <span v-else class="select-placeholder">{{ placeholder }}</span>
        </div>

        <!-- Clear button -->
        <button
          v-if="clearable && modelValue && !disabled"
          type="button"
          class="select-clear"
          @click.stop="handleClear"
          aria-label="Очистить"
        >
          <XIcon :size="16" />
        </button>

        <!-- Dropdown arrow -->
        <ChevronDownIcon
          :size="20"
          class="select-arrow"
          :class="{ 'select-arrow--open': isOpen }"
        />
      </div>

      <!-- Dropdown menu -->
      <Teleport to="body">
        <Transition name="select-menu">
          <div v-if="isOpen" ref="menuRef" class="select-menu" :style="menuStyles" @click.stop>
            <!-- Search input -->
            <div v-if="searchable" class="select-search">
              <SearchIcon :size="16" class="select-search-icon" />
              <input
                ref="searchInputRef"
                v-model="searchQuery"
                type="text"
                class="select-search-input"
                :placeholder="searchPlaceholder"
                @keydown="handleSearchKeydown"
              />
            </div>

            <!-- Options list -->
            <div class="select-options" ref="optionsRef">
              <!-- Loading state -->
              <div v-if="loading" class="select-loading">
                <div class="select-loading-spinner" />
                <span>Загрузка...</span>
              </div>

              <!-- Empty state -->
              <div v-else-if="filteredOptions.length === 0" class="select-empty">
                {{ emptyText }}
              </div>

              <!-- Options -->
              <template v-else>
                <div
                  v-for="(option, index) in filteredOptions"
                  :key="option.value"
                  class="select-option"
                  :class="{
                    'select-option--selected': isSelected(option),
                    'select-option--highlighted': highlightedIndex === index,
                    'select-option--disabled': option.disabled,
                  }"
                  @click="!option.disabled && handleSelect(option)"
                  @mouseenter="highlightedIndex = index"
                >
                  <component
                    v-if="option.icon"
                    :is="option.icon"
                    :size="16"
                    class="select-option-icon"
                  />
                  <span class="select-option-label">{{ option.label }}</span>
                  <CheckIcon v-if="isSelected(option)" :size="16" class="select-option-check" />
                </div>
              </template>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>

    <!-- Helper text or error -->
    <div v-if="helperText || errorMessage" class="select-helper">
      <span v-if="errorMessage" class="select-error">{{ errorMessage }}</span>
      <span v-else class="select-hint">{{ helperText }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { ChevronDownIcon, XIcon, SearchIcon, CheckIcon } from 'lucide-vue-next'
import { onClickOutside } from '@vueuse/core'

export interface SelectOption {
  value: string | number
  label: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any
  disabled?: boolean
}

export interface AppSelectProps {
  /** Значение */
  modelValue?: string | number | null
  /** Опции выбора */
  options: SelectOption[]
  /** Label поля */
  label?: string
  /** Placeholder */
  placeholder?: string
  /** Helper text */
  helperText?: string
  /** Error message */
  errorMessage?: string
  /** Обязательное поле */
  required?: boolean
  /** Отключено */
  disabled?: boolean
  /** Возможность очистки */
  clearable?: boolean
  /** Возможность поиска */
  searchable?: boolean
  /** Placeholder для поиска */
  searchPlaceholder?: string
  /** Текст при отсутствии опций */
  emptyText?: string
  /** Загрузка */
  loading?: boolean
  /** Размер */
  size?: 'small' | 'medium' | 'large'
  /** Вариант */
  variant?: 'outline' | 'filled'
}

const props = withDefaults(defineProps<AppSelectProps>(), {
  placeholder: 'Выберите значение',
  searchPlaceholder: 'Поиск...',
  emptyText: 'Ничего не найдено',
  size: 'medium',
  variant: 'outline',
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null]
  change: [value: string | number | null]
  search: [query: string]
}>()

// Refs
const selectRef = ref<HTMLElement>()
const menuRef = ref<HTMLElement>()
const searchInputRef = ref<HTMLInputElement>()
const optionsRef = ref<HTMLElement>()

// State
const isOpen = ref(false)
const searchQuery = ref('')
const highlightedIndex = ref(-1)
const menuPosition = ref({ top: 0, left: 0, width: 0 })

// Generate unique ID for input
const inputId = `select-${Math.random().toString(36).substr(2, 9)}`

// Computed
const hasError = computed(() => !!props.errorMessage)

const selectClasses = computed(() => [`app-select--${props.size}`, `app-select--${props.variant}`])

const selectedOption = computed(() => {
  return props.options.find((opt) => opt.value === props.modelValue)
})

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) {
    return props.options
  }

  const query = searchQuery.value.toLowerCase()
  return props.options.filter((option) => option.label.toLowerCase().includes(query))
})

const menuStyles = computed(() => ({
  top: `${menuPosition.value.top}px`,
  left: `${menuPosition.value.left}px`,
  width: `${menuPosition.value.width}px`,
}))

// Methods
const calculateMenuPosition = () => {
  if (!selectRef.value || !menuRef.value) return

  const rect = selectRef.value.getBoundingClientRect()
  const menuHeight = menuRef.value.offsetHeight
  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top

  let top = rect.bottom + 4

  // If not enough space below and more space above, show menu above
  if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
    top = rect.top - menuHeight - 4
  }

  menuPosition.value = {
    top,
    left: rect.left,
    width: rect.width,
  }
}

const open = async () => {
  if (props.disabled || isOpen.value) return

  isOpen.value = true
  searchQuery.value = ''
  highlightedIndex.value = -1

  await nextTick()
  calculateMenuPosition()

  if (props.searchable && searchInputRef.value) {
    searchInputRef.value.focus()
  }
}

const close = () => {
  if (!isOpen.value) return

  isOpen.value = false
  searchQuery.value = ''
  highlightedIndex.value = -1
}

const handleInputClick = () => {
  if (isOpen.value) {
    close()
  } else {
    open()
  }
}

const handleSelect = (option: SelectOption) => {
  emit('update:modelValue', option.value)
  emit('change', option.value)
  close()
}

const handleClear = () => {
  emit('update:modelValue', null)
  emit('change', null)
}

const isSelected = (option: SelectOption) => {
  return option.value === props.modelValue
}

const handleSearchKeydown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      highlightedIndex.value = Math.min(
        highlightedIndex.value + 1,
        filteredOptions.value.length - 1,
      )
      scrollToHighlighted()
      break
    case 'ArrowUp':
      event.preventDefault()
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
      scrollToHighlighted()
      break
    case 'Enter':
      event.preventDefault()
      if (highlightedIndex.value >= 0) {
        const option = filteredOptions.value[highlightedIndex.value]
        if (option && !option.disabled) {
          handleSelect(option)
        }
      }
      break
    case 'Escape':
      close()
      break
  }
}

const scrollToHighlighted = () => {
  if (!optionsRef.value || highlightedIndex.value < 0) return

  const options = optionsRef.value.querySelectorAll('.select-option')
  const highlighted = options[highlightedIndex.value] as HTMLElement

  if (highlighted) {
    highlighted.scrollIntoView({ block: 'nearest' })
  }
}

// Watchers
watch(searchQuery, (query) => {
  emit('search', query)
})

// Click outside handler
onClickOutside(menuRef, close, { ignore: [selectRef] })

// Window events
const handleResize = () => {
  if (isOpen.value) {
    calculateMenuPosition()
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  window.addEventListener('scroll', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', handleResize)
})
</script>

<style scoped>
.app-select {
  position: relative;
  width: 100%;
}

/* Label */
.select-label {
  display: block;
  margin-bottom: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-primary);
}

.select-required {
  color: var(--color-error);
  margin-left: 0.25rem;
}

/* Input wrapper */
.select-wrapper {
  position: relative;
}

.select-input {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 2.625rem;
  padding: 0.5rem 2.5rem 0.5rem 0.875rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md, 0.625rem);
  cursor: pointer;
  transition: all 0.15s ease;
}

/* Sizes */
.app-select--small .select-input {
  min-height: 2rem;
  padding: 0.375rem 2rem 0.375rem 0.625rem;
  font-size: 0.8125rem;
}

.app-select--large .select-input {
  min-height: 3rem;
  padding: 0.75rem 3rem 0.75rem 1rem;
  font-size: 1rem;
}

/* Variants */
.app-select--filled .select-input {
  background: var(--color-background-secondary);
  border-color: transparent;
}

/* States */
.select-input:hover:not(.select-input--disabled) {
  border-color: var(--color-border-default);
}

.select-input--focused {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-focus);
}

.select-input--error {
  border-color: var(--color-danger);
}

.select-input--error.select-input--focused {
  box-shadow: 0 0 0 3px rgba(244, 63, 94, 0.12);
}

.select-input--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--color-background-secondary);
}

/* Value */
.select-value {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;
}

.select-value-icon {
  flex-shrink: 0;
  color: var(--color-text-secondary);
}

.select-value-text {
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-placeholder {
  color: var(--color-text-tertiary);
}

/* Clear button */
.select-clear {
  position: absolute;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
  transition: var(--transition-all, all 150ms);
}

.select-clear:hover {
  color: var(--color-text-primary);
}

/* Arrow */
.select-arrow {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-secondary);
  pointer-events: none;
  transition: transform 200ms;
}

.select-arrow--open {
  transform: translateY(-50%) rotate(180deg);
}

/* Dropdown menu */
.select-menu {
  position: fixed;
  z-index: 10000; /* Higher than modal (9999) */
  max-height: 320px;
  background: var(--color-background-elevated);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md, 0.5rem);
  box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Search */
.select-search {
  position: relative;
  padding: 0.5rem;
  border-bottom: 1px solid var(--color-border-light);
}

.select-search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-tertiary);
  pointer-events: none;
}

.select-search-input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  background: var(--color-background-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-sm, 0.375rem);
  font-size: 0.875rem;
  color: var(--color-text-primary);
  transition: var(--transition-all, all 150ms);
}

.select-search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

/* Options */
.select-options {
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem 0;
}

.select-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  transition: var(--transition-all, all 150ms);
  user-select: none;
}

.select-option:hover:not(.select-option--disabled) {
  background: var(--color-background-secondary);
}

.select-option--highlighted {
  background: var(--color-background-secondary);
}

.select-option--selected {
  color: var(--color-primary);
  background: var(--color-primary-subtle);
}

.select-option--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.select-option-icon {
  flex-shrink: 0;
  color: var(--color-text-secondary);
}

.select-option-label {
  flex: 1;
  font-size: 0.875rem;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-option-check {
  flex-shrink: 0;
  margin-left: auto;
  color: var(--color-primary);
}

/* Loading */
.select-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
}

.select-loading-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--color-border-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty */
.select-empty {
  padding: 2rem;
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: 0.875rem;
}

/* Helper text */
.select-helper {
  margin-top: 0.375rem;
  font-size: 0.8125rem;
}

.select-hint {
  color: var(--color-text-secondary);
}

.select-error {
  color: var(--color-error);
}

/* Menu animation */
.select-menu-enter-active,
.select-menu-leave-active {
  transition:
    opacity 200ms,
    transform 200ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.select-menu-enter-from,
.select-menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Scrollbar styling */
.select-options::-webkit-scrollbar {
  width: 6px;
}

.select-options::-webkit-scrollbar-track {
  background: transparent;
}

.select-options::-webkit-scrollbar-thumb {
  background: var(--color-border-light);
  border-radius: 3px;
}

.select-options::-webkit-scrollbar-thumb:hover {
  background: var(--color-border-default);
}
</style>
